'use client';

import React, { useMemo, useState } from 'react';

import {
    Icon,
    IconName,
    RadioTile,
    RadioTiles,
    Row,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    TypographyBold,
} from '@bytel/trilogy-react';

import { filterPromotionsByTypesMarketing } from '@services/products';

import { MobilePlanType } from '@app-types/plan';
import { UserType } from '@app-types/user';

import { Big } from '@components/big';
import { InlinePrice } from '@components/inline-price';
import { PromoHomeModal } from '@components/promo-home-modal';

import { MarketingsState, PartialMarketingsState } from '@hooks/use-marketings';

import { MarketingType } from '@constants/plan';

import { sendProductConvergenceTracking, updatePromos } from '@product-detail/helpers';

export type PromoHomeProps = {
    currentPlan?: MobilePlanType;
    setMarketings: (marketings: PartialMarketingsState) => MarketingsState;
    marketings: MarketingsState;
    user?: UserType;
    isRenewal: boolean;
};

export function PromoHome({ currentPlan, marketings, setMarketings, user, isRenewal }: Readonly<PromoHomeProps>) {
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const updatePromo = updatePromos(marketings, setMarketings, sendProductConvergenceTracking, isRenewal);

    const promoHomeDiscount = useMemo(
        () =>
            filterPromotionsByTypesMarketing(
                currentPlan?.details?.discounts || [],
                [MarketingType.HOME],
                false,
                true,
            )?.[0]?.value || 0,
        [currentPlan],
    );

    if (!currentPlan) return null;

    const promos = filterPromotionsByTypesMarketing(
        currentPlan.details?.discounts || [],
        [MarketingType.HOME],
        true,
        true,
    );

    // HOME radio logic:
    // isIncitation=true (mobile customer) -> HOME not applied -> "Non" selected
    // isIncitation=false (FAI customer)   -> HOME applied     -> "Oui" selected
    // When connected, only the selected option stays enabled.
    const isConnected = user !== undefined;
    const isHomeApplied = promos[0]?.isIncitation === false;
    const isHomeSelected = isConnected ? isHomeApplied : marketings[MarketingType.HOME];

    if (promos.length === 0 || isRenewal) return null;

    return (
        <Row data-cy='promo-home'>
            <Text level={TextLevels.ONE} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD} marginless>
                Vous êtes déjà client Bbox&nbsp;?
                <Icon name={IconName.INFOS_CIRCLE} onClick={() => setIsModalOpen(true)} className='cursor-pointer' />
            </Text>
            <Spacer size={SpacerSize.TWO} />
            <Text>
                {promoHomeDiscount > 0 ? (
                    <>
                        <span aria-label="Je bénéficie d'un big économie de">
                            Je bénéficie d&apos;une <Big /> économie de{' '}
                        </span>
                        <InlinePrice amount={-promoHomeDiscount} period='mois' /> sur mon forfait.
                    </>
                ) : (
                    <>Je bénéficie de prix exclusifs sur mon forfait.</>
                )}
            </Text>
            <Spacer size={SpacerSize.FOUR} />
            <PromoHomeModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
            <RadioTiles className='grid-radio-list'>
                <RadioTile
                    id='convergence-yes'
                    name='convergence'
                    horizontal
                    disabled={isConnected && !isHomeSelected}
                    label='Oui'
                    onChange={() => updatePromo(true, [MarketingType.HOME])}
                    checked={isHomeSelected}
                />
                <RadioTile
                    id='convergence-no'
                    name='convergence'
                    horizontal
                    disabled={isConnected && isHomeSelected}
                    label='Non'
                    onChange={() => updatePromo(false, [MarketingType.HOME])}
                    checked={!isHomeSelected}
                />
            </RadioTiles>
        </Row>
    );
}
